import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Mail, Lock, User, Building2, ArrowRight, Gift, Crown, Check } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Spinner } from '@/components/bits';

const C = {
  bg: '#050816', card: '#111A33', blue: '#2F7CFF', gold: '#F4B72A',
  white: '#F8FAFC', border: 'rgba(255,255,255,0.08)', muted: '#8B93B0', purple: '#7C4DFF',
};

const AVATAR = 'https://static.prod-images.emergentagent.com/jobs/2d4b492c-5368-49c7-a1a1-292705288391/images/0f3b07842e08b90773a0d157e642949e49c4e17bc750df4e3d98cf870c42d516.jpeg';

const MINI_STATS = [
  { target: 10000, suffix: '+', label: 'Creators', color: C.gold },
  { target: 95, suffix: '%', label: 'Automated', color: C.purple },
  { target: 100, suffix: '%', label: 'Secure', color: C.blue },
];

function CountUp({ target, suffix }) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    const dur = 1400, t0 = performance.now();
    const tick = (now) => {
      const p = Math.min((now - t0) / dur, 1);
      setVal(Math.round(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) requestAnimationFrame(tick);
    };
    const id = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(id);
  }, [target]);
  return <span>{val.toLocaleString()}{suffix}</span>;
}

const PERKS = ['20 free credits on signup', 'App Builder + Creative Studio', 'Myra Marketing AI'];

export default function Signup() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', company: '', password: '' });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await signup(form); toast.success('Account created — 20 free credits added! 🎉'); navigate('/'); }
    catch (err) { toast.error(err.message || 'Signup failed'); }
    finally { setBusy(false); }
  };

  const iw = { display: 'flex', alignItems: 'center', gap: 10, background: '#0A1020', border: `1px solid ${C.border}`, borderRadius: 14, padding: '13px 15px', marginBottom: 16 };
  const inp = { background: 'transparent', border: 'none', outline: 'none', color: C.white, fontSize: 14, width: '100%' };

  return (
    <div style={{ minHeight: '100vh', background: C.bg, position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none', background: `radial-gradient(800px 500px at 20% 0%, rgba(47,124,255,0.16), transparent 60%), radial-gradient(700px 500px at 85% 100%, rgba(244,183,42,0.12), transparent 60%)` }} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center" style={{ position: 'relative', zIndex: 2, maxWidth: 980, width: '100%' }}>
        {/* left pitch */}
        <div className="hidden lg:block float-in">
          <div className="flex items-center gap-4 mb-6">
            <div style={{ position: 'relative' }}>
              <div style={{ position: 'absolute', inset: -8, borderRadius: '50%', background: 'radial-gradient(circle, rgba(47,124,255,0.35), transparent 70%)' }} className="pulse-glow" />
              <img src={AVATAR} alt="Myra" style={{ position: 'relative', width: 84, height: 84, borderRadius: '50%', objectFit: 'cover', objectPosition: 'top', border: `2px solid ${C.gold}` }} />
            </div>
            <div>
              <div style={{ color: C.white, fontWeight: 800, fontSize: 16 }}>Hi, I'm Myra 👋</div>
              <div style={{ color: C.muted, fontSize: 13 }}>Your AI Creative Director</div>
            </div>
          </div>
          <div className="inline-flex items-center gap-2 mb-6" style={{ padding: '7px 16px', borderRadius: 999, border: `1px solid ${C.gold}`, background: 'rgba(244,183,42,0.08)' }}>
            <Crown size={14} color={C.gold} /><span style={{ color: C.gold, fontSize: 11, fontWeight: 800, letterSpacing: 1.5 }}>JOIN MYRAAI STUDIO</span>
          </div>
          <h1 className="font-display" style={{ fontSize: 48, fontWeight: 900, lineHeight: 1.05, color: C.white, letterSpacing: -1 }}>
            Start building<br /><span style={{ background: `linear-gradient(100deg,${C.blue},${C.gold})`, WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>in seconds.</span>
          </h1>
          <p style={{ color: C.muted, fontSize: 16, lineHeight: 1.7, marginTop: 18, maxWidth: 420 }}>
            One account connects two powerful platforms — MYRA AI Marketing and MYRA Software Coding.
          </p>
          <ul style={{ marginTop: 22, display: 'grid', gap: 12 }}>
            {PERKS.map((p) => (
              <li key={p} className="flex items-center gap-3" style={{ color: C.white, fontSize: 15 }}>
                <span style={{ width: 24, height: 24, borderRadius: 999, background: `linear-gradient(135deg,${C.gold},#D69A12)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Check size={14} color="#2A1E02" /></span>{p}
              </li>
            ))}
          </ul>
          <div className="flex gap-6" style={{ marginTop: 28 }}>
            {MINI_STATS.map((s) => (
              <div key={s.label}>
                <div className="font-display" style={{ fontSize: 26, fontWeight: 900, color: s.color }}><CountUp target={s.target} suffix={s.suffix} /></div>
                <div style={{ color: C.muted, fontSize: 12 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* signup card */}
        <div className="float-in" style={{ background: 'rgba(17,26,51,0.7)', backdropFilter: 'blur(20px)', border: `1px solid ${C.border}`, borderRadius: 26, padding: 30, boxShadow: '0 24px 70px rgba(0,0,0,0.5)' }}>
          <div className="flex items-center gap-3 mb-2">
            <div style={{ width: 42, height: 42, borderRadius: 12, background: `linear-gradient(135deg,#FBD46A,${C.gold},#C98A0E)`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#2A1E02', fontWeight: 900, fontFamily: 'Playfair Display, serif', fontSize: 22 }}>M</div>
            <div className="font-display" style={{ fontSize: 22, fontWeight: 900, color: C.white }}>Join <span style={{ color: C.gold }}>MyRaai</span></div>
          </div>
          <div className="flex items-center gap-2" style={{ background: 'rgba(47,124,255,0.1)', border: '1px solid rgba(47,124,255,0.3)', borderRadius: 12, padding: '9px 12px', margin: '14px 0 20px' }}>
            <Gift size={16} color={C.blue} /><span style={{ color: '#9DC1FF', fontSize: 12.5, fontWeight: 600 }}>Get 20 free credits on signup</span>
          </div>
          <form onSubmit={submit}>
            <div style={iw}><User size={16} color={C.muted} /><input data-testid="signup-name" required minLength={2} value={form.name} onChange={set('name')} placeholder="Full name" style={inp} /></div>
            <div style={iw}><Mail size={16} color={C.muted} /><input data-testid="signup-email" type="email" required value={form.email} onChange={set('email')} placeholder="you@brand.com" style={inp} /></div>
            <div style={iw}><Building2 size={16} color={C.muted} /><input data-testid="signup-company" value={form.company} onChange={set('company')} placeholder="Company (optional)" style={inp} /></div>
            <div style={{ ...iw, marginBottom: 22 }}><Lock size={16} color={C.muted} /><input data-testid="signup-password" type="password" required minLength={6} value={form.password} onChange={set('password')} placeholder="Password (min 6 chars)" style={inp} /></div>
            <button data-testid="signup-submit" type="submit" disabled={busy} className="luxe-btn font-bold flex items-center justify-center gap-2" style={{ width: '100%', background: `linear-gradient(135deg,#FBD46A,${C.gold},#D69A12)`, color: '#2A1E02', borderRadius: 14, padding: '14px', boxShadow: '0 10px 30px rgba(244,183,42,0.4)' }}>
              {busy ? <span className="flex items-center gap-2"><Spinner color="#2A1E02" /> Creating…</span> : <>Create Account <ArrowRight size={16} /></>}
            </button>
          </form>
          <div className="text-center" style={{ marginTop: 18, color: C.muted, fontSize: 13 }}>
            Already have an account? <Link to="/login" data-testid="go-login" style={{ color: C.blue, fontWeight: 700 }}>Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
